/*
 * Home page motion. Loaded only by the sections that use it (defer).
 * Every effect runs once, moves only transform / opacity / clip-path, and is skipped
 * when the visitor prefers reduced motion. Content and links are server-rendered;
 * without this file the sections stay fully usable.
 */
(() => {
  if (window.__homeMotion) return;
  window.__homeMotion = true;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const design = !!(window.Shopify && window.Shopify.designMode);
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };

  /* Hero, "heading crosses into the photo": the heading's colour changes exactly at the photo's edge.
     Measures the photo against the heading and stores the distance as --split (px). */
  define('hero-crossing', class extends HTMLElement {
    connectedCallback() {
      this.hero = this.closest('.hero');
      this.media = this.hero && this.hero.querySelector('.hero__media');
      this.heading = this.hero && this.hero.querySelector('.hero__heading');
      if (!this.media || !this.heading) return;
      const measure = () => {
        const m = this.media.getBoundingClientRect();
        const h = this.heading.getBoundingClientRect();
        this.hero.style.setProperty('--split-x', `${Math.round(m.left - h.left)}px`);
        this.hero.style.setProperty('--split-y', `${Math.round(m.bottom - h.top)}px`);
      };
      measure();
      if ('ResizeObserver' in window) { this.ro = new ResizeObserver(measure); this.ro.observe(this.hero); this.ro.observe(this.heading); }
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
    }
    disconnectedCallback() { if (this.ro) this.ro.disconnect(); }
  });

  /* Watch finder: brand select fills the model select; the link and its count follow. */
  define('watch-finder', class extends HTMLElement {
    connectedCallback() {
      this.brandSel = this.querySelector('[data-brand]');
      this.modelSel = this.querySelector('[data-model]');
      this.go = this.querySelector('[data-go]');
      this.count = this.querySelector('[data-count]');
      this.data = [...this.querySelectorAll('[data-brand-item]')].map((li) => {
        const brand = li.querySelector(':scope > a');
        const models = [...li.querySelectorAll('[data-model-item]')].map((a) => ({ name: a.textContent.trim(), url: a.href, count: a.dataset.count }));
        return { url: brand.href, count: brand.dataset.count, models };
      });
      if (!this.data.length) return;
      this.classList.add('is-ready');
      this.brandSel.addEventListener('change', () => this.fillModels());
      this.modelSel.addEventListener('change', () => this.update());
      this.fillModels(true);
    }
    fillModels(first) {
      const b = this.data[this.brandSel.value] || this.data[0];
      this.querySelectorAll('[data-dial]').forEach((d, i) => d.classList.toggle('is-active', String(i) === String(this.brandSel.value)));
      this.modelSel.replaceChildren(...b.models.map((m, i) => new Option(m.name, i)));
      this.modelSel.hidden = b.models.length === 0;
      this.update(first);
    }
    update(first) {
      const b = this.data[this.brandSel.value] || this.data[0];
      const m = b.models.length ? b.models[this.modelSel.value || 0] : b;
      this.go.href = m.url;
      this.setCount(m.count, first);
    }
    setCount(n, instant) {
      const old = this.count.firstElementChild;
      if (old && old.textContent === String(n)) return;
      const next = document.createElement('span');
      next.textContent = n;
      if (instant || reduce.matches || !old) { this.count.replaceChildren(next); return; }
      next.className = 'is-below';
      this.count.append(next);
      requestAnimationFrame(() => requestAnimationFrame(() => { old.className = 'is-above'; next.className = ''; }));
      setTimeout(() => old.remove(), 520);
    }
  });

  /* Strap showcase: large photo wipes in, caption lines swap; thumbnail rail, arrows, swipe, arrow keys. */
  define('strap-showcase', class extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('.strap-showcase__item')].map((li) => ({
        img: li.querySelector('img'), label: li.dataset.label, leather: li.dataset.leather, price: li.dataset.price, url: li.dataset.url
      })).filter((i) => i.img);
      if (!this.items.length) return;
      this.stage = this.querySelector('[data-stage]');
      this.caption = this.querySelector('[data-caption]');
      this.thumbs = this.querySelector('[data-thumbs]');
      this.cur = -1;
      this.busy = false;

      this.thumbs.replaceChildren(...this.items.map((it, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'strap-showcase__thumb';
        b.setAttribute('aria-pressed', 'false');
        b.setAttribute('aria-label', `${it.label}, ${it.leather}, ${it.price}`);
        const t = it.img.cloneNode();
        t.alt = ''; t.sizes = '80px'; t.loading = 'lazy';
        b.append(t);
        b.addEventListener('click', () => this.show(i));
        return b;
      }));
      this.thumbs.addEventListener('keydown', (e) => {
        const d = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
        if (!d) return;
        e.preventDefault();
        this.show(this.cur + d);
        this.thumbs.children[this.cur].focus();
      });
      this.querySelector('[data-prev]').addEventListener('click', () => this.show(this.cur - 1));
      this.querySelector('[data-next]').addEventListener('click', () => this.show(this.cur + 1));
      let x0 = null;
      this.stage.addEventListener('pointerdown', (e) => { x0 = e.clientX; });
      this.stage.addEventListener('pointerup', (e) => {
        if (x0 !== null && Math.abs(e.clientX - x0) > 40) this.show(this.cur + (e.clientX < x0 ? 1 : -1));
        x0 = null;
      });

      this.classList.add('is-ready');
      this.stage.hidden = false; this.caption.hidden = false; this.thumbs.hidden = false;
      this.show(0);
      // Fetch the other large photos once the section is near, so a wipe never reveals an empty frame.
      const io = new IntersectionObserver((es) => {
        if (!es[0].isIntersecting) return;
        io.disconnect();
        this.items.forEach((it) => { const im = new Image(); im.sizes = '(min-width: 990px) 48vw, 100vw'; im.srcset = it.img.srcset; im.src = it.img.src; });
      }, { rootMargin: '400px 0px' });
      io.observe(this);
    }
    show(i) {
      const n = this.items.length;
      i = (i + n) % n;
      if (i === this.cur || this.busy) return;
      const it = this.items[i];
      const first = this.cur < 0 || reduce.matches;
      const img = it.img.cloneNode();
      img.loading = 'eager';
      img.sizes = '(min-width: 990px) 48vw, 100vw';
      this.stage.append(img);
      const clear = () => [...this.stage.children].slice(0, -1).forEach((c) => c.remove());
      if (first) {
        clear();
      } else {
        this.busy = true;
        img.classList.add('is-entering');
        const end = () => { clear(); img.classList.remove('is-entering'); this.busy = false; };
        img.addEventListener('animationend', end, { once: true });
        setTimeout(() => { if (this.busy) end(); }, 1400);
      }
      const fill = () => {
        this.caption.querySelector('[data-c-label]').textContent = it.label;
        this.caption.querySelector('[data-c-leather]').textContent = it.leather;
        this.caption.querySelector('[data-c-price]').textContent = it.price;
        this.caption.querySelector('[data-c-url]').href = it.url;
      };
      if (first) fill();
      else { this.caption.classList.add('is-swapping'); setTimeout(() => { fill(); this.caption.classList.remove('is-swapping'); }, 300); }
      [...this.thumbs.children].forEach((b, j) => b.setAttribute('aria-pressed', String(j === i)));
      this.cur = i;
    }
  });

  /* Shop by Watch, editorial index: the frame shows the brand under the pointer or focus. */
  define('brand-index', class extends HTMLElement {
    connectedCallback() {
      const imgs = [...this.querySelectorAll('[data-peek-img]')];
      this.querySelectorAll('[data-peek]').forEach((a) => {
        // A brand without its own portrait keeps the last photo rather than showing an empty frame.
        const on = () => { const t = imgs[Number(a.dataset.peek)]; if (!t || !t.querySelector('img')) return; imgs.forEach((m) => m.classList.toggle('is-active', m === t)); };
        a.addEventListener('mouseenter', on);
        a.addEventListener('focus', on);
      });
    }
  });

  /* Leather index: the list drives the large photo; a loupe follows fine pointers to show the grain. */
  define('leather-index', class extends HTMLElement {
    connectedCallback() {
      const items = [...this.querySelectorAll('[data-leather]')];
      const photos = [...this.querySelectorAll('[data-leather-photo]')];
      items.forEach((li, i) => {
        const on = () => { items.forEach((x) => x.classList.toggle('is-active', x === li)); photos.forEach((p, j) => p.classList.toggle('is-active', j === i)); };
        li.addEventListener('mouseenter', on);
        li.addEventListener('focusin', on);
      });
      const stage = this.querySelector('.leather-index__stage');
      const loupe = this.querySelector('[data-loupe]');
      if (!stage || !loupe || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      const Z = 2.4, R = 80;
      let raf = 0;
      stage.addEventListener('pointerenter', () => stage.classList.add('is-zooming'));
      stage.addEventListener('pointerleave', () => stage.classList.remove('is-zooming'));
      stage.addEventListener('pointermove', (e) => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const img = stage.querySelector('.leather-index__photo.is-active img');
          if (!img || !img.naturalWidth) return;
          const r = stage.getBoundingClientRect();
          const x = e.clientX - r.left, y = e.clientY - r.top;
          // match object-fit: cover, so the loupe shows exactly the grain under the pointer
          const k = Math.max(r.width / img.naturalWidth, r.height / img.naturalHeight);
          const rw = img.naturalWidth * k, rh = img.naturalHeight * k, ox = (r.width - rw) / 2, oy = (r.height - rh) / 2;
          loupe.style.backgroundImage = `url("${img.currentSrc || img.src}")`;
          loupe.style.left = `${x}px`; loupe.style.top = `${y}px`;
          loupe.style.backgroundSize = `${rw * Z}px ${rh * Z}px`;
          loupe.style.backgroundPosition = `${-(x - ox) * Z + R}px ${-(y - oy) * Z + R}px`;
        });
      });
    }
  });

  /* Collector card: guilloché drawn once in view; a small tilt + sheen on fine pointers only. */
  define('collector-card', class extends HTMLElement {
    connectedCallback() {
      if (reduce.matches || design || !('IntersectionObserver' in window)) { this.classList.add('is-in'); return; }
      const io = new IntersectionObserver((es) => { if (es[0].isIntersecting) { this.classList.add('is-in'); io.disconnect(); } }, { threshold: 0.3 });
      io.observe(this);
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      const host = this.parentElement;
      let raf = 0;
      host.addEventListener('pointermove', (e) => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const r = this.getBoundingClientRect();
          const x = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
          const y = Math.min(1, Math.max(0, (e.clientY - r.top) / r.height));
          this.style.setProperty('--ry', `${((x - 0.5) * 8).toFixed(2)}deg`);
          this.style.setProperty('--rx', `${((0.5 - y) * 6).toFixed(2)}deg`);
          this.style.setProperty('--mx', `${(x * 100).toFixed(1)}%`);
          this.style.setProperty('--my', `${(y * 100).toFixed(1)}%`);
        });
      });
      host.addEventListener('pointerleave', () => { this.style.setProperty('--rx', '0deg'); this.style.setProperty('--ry', '0deg'); });
    }
  });
})();
